import { FC, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, X } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type SingleDatePickerProps = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  triggerVariant?: ButtonProps['variant'];
  badgeText?: string;
  fromDate?: Date;
  toDate?: Date;
};

const getDateLabel = (date: Date | undefined, placeholder: string) => {
  if (!date) return placeholder;
  return format(date, 'dd/MM/yyyy');
};

const SingleDatePicker: FC<SingleDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  className,
  align = 'start',
  triggerVariant = 'outline',
  badgeText,
  fromDate,
  toDate,
}) => {
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Date | undefined>(value);
  const [month, setMonth] = useState<Date>(value ?? new Date());

  useEffect(() => {
    if (!open) {
      setDraftDate(value);
      setMonth(value ?? new Date());
    }
  }, [open, value]);

  const dateLabel = useMemo(() => getDateLabel(value, placeholder), [placeholder, value]);
  const draftDateLabel = useMemo(() => getDateLabel(draftDate, 'Elegí una fecha'), [draftDate]);

  const applyDate = () => {
    onChange(draftDate);
    setOpen(false);
  };

  const clearDraftDate = () => {
    setDraftDate(undefined);
    setMonth(new Date());
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex flex-col items-start">
            <Button
              variant={triggerVariant}
              className={cn(
                'h-11 w-full justify-start rounded-xl px-4 text-left font-normal shadow-sm',
                !value && 'text-muted-foreground',
                className,
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
              <span className="truncate">{dateLabel}</span>
            </Button>
            {badgeText && value ? (
              <span className="rounded-b-[12px] bg-teal-600 px-5 py-2 text-[11px] text-white">
                {badgeText}
              </span>
            ) : null}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl"
          align={align}
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          sticky="always"
          style={{
            width: 'min(18rem, calc(100vw - 1rem), var(--radix-popover-content-available-width))',
            maxHeight: 'min(70vh, var(--radix-popover-content-available-height))',
          }}
        >
          <div className="space-y-3 p-3">
            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <CalendarIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Fecha seleccionada
                  </p>
                  <p className="truncate text-[13px] font-medium text-slate-900">
                    {draftDateLabel}
                  </p>
                </div>
                {draftDate ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={clearDraftDate}
                    className="h-8 w-8 shrink-0 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Limpiar fecha"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 p-2">
              <Calendar
                mode="single"
                showOutsideDays
                fixedWeeks
                selected={draftDate}
                onSelect={(date) => {
                  setDraftDate(date);
                  if (date) {
                    setMonth(date);
                  }
                }}
                month={month}
                onMonthChange={setMonth}
                fromDate={fromDate}
                toDate={toDate}
                locale={es}
                className="p-0"
                classNames={{
                  months: 'flex flex-col gap-2.5',
                  month: 'min-w-0 space-y-2.5',
                  caption:
                    'relative flex h-8 items-center justify-center rounded-xl border border-slate-200 bg-white px-7 shadow-sm',
                  caption_label:
                    'text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700',
                  nav_button:
                    'absolute h-5 w-5 rounded-lg border-0 bg-transparent p-0 text-slate-400 opacity-100 hover:bg-slate-100 hover:text-slate-700',
                  nav_button_previous: 'left-1.5',
                  nav_button_next: 'right-1.5',
                  table: 'w-full border-collapse',
                  head_row: 'flex w-full',
                  head_cell:
                    'w-8 rounded-md text-[0.6rem] font-medium uppercase tracking-[0.1em] text-slate-400',
                  row: 'mt-0.5 flex w-full',
                  cell: 'relative h-8 w-8 p-0 text-center text-[11px] [&:has([aria-selected])]:bg-primary/10 [&:has([aria-selected])]:rounded-lg',
                  day: 'h-8 w-8 rounded-lg p-0 text-[11px] font-medium text-slate-700 hover:bg-slate-100',
                  day_selected:
                    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                  day_today: 'bg-slate-100 text-slate-900',
                  day_outside: 'text-slate-300 opacity-100',
                  day_disabled: 'text-slate-300 opacity-100',
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50/80 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-slate-500">
              {draftDate
                ? `Seleccionaste ${format(draftDate, 'dd/MM/yyyy')}`
                : 'No hay fecha seleccionada.'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="h-8 rounded-xl px-3 text-slate-600 hover:bg-white hover:text-slate-900"
              >
                Cancelar
              </Button>
              <Button type="button" size="sm" onClick={applyDate} className="h-8 rounded-xl px-4">
                Aplicar
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SingleDatePicker;
